/**
 * Global Pantry — Ingredient Registry (Admin-only tab)
 * Master list of raw materials with current market prices.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Search, Edit2, Trash2, Upload, Download, Check, X, Loader2, Package, AlertTriangle, ChevronDown } from 'lucide-react';
import { apiFetch } from '../../../data/ApiModuleRepository';
import type { Ingredient, PurchaseUnit } from '../types';
import { PURCHASE_UNITS } from '../types';
import { cn } from '../../../ui/cn';
import { useToast } from '../../../ui/Toast';
import type { User } from '../../../domain/entities';

// ─── Helpers ────────────────────────────────────────────────────────────────

const INGREDIENT_CATEGORIES = [
  'Proteins', 'Vegetables', 'Fruits', 'Dairy', 'Grains & Starches',
  'Oils & Fats', 'Spices & Seasonings', 'Sauces & Condiments',
  'Beverages', 'Packaging', 'Other',
];

interface PriceChange {
  id: number;
  ingredient_id: string;
  ingredient_name: string;
  old_price: number;
  new_price: number;
  changed_at: string;
}

// ─── Main Component ────────────────────────────────────────────────────────

export function PantryTab({ user }: { user: User | null }) {
  const toast = useToast();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [editing, setEditing] = useState<Ingredient | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [priceChanges, setPriceChanges] = useState<PriceChange[]>([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Load data ──
  const load = async () => {
    try {
      const res = await apiFetch<{ data: Ingredient[] }>('/api/menu/ingredients');
      setIngredients(res.data || []);
    } catch { toast.error('Load Failed', 'Could not load ingredients.'); }
    setLoading(false);
  };

  const loadAlerts = async () => {
    try {
      const res = await apiFetch<{ data: PriceChange[] }>('/api/menu/ingredients/price-changes');
      setPriceChanges(res.data || []);
    } catch { /* Silent */ }
  };

  useEffect(() => { load(); loadAlerts(); }, []);

  // ── Filtered list ──
  const filtered = useMemo(() => {
    let list = ingredients;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i => i.name.toLowerCase().includes(q));
    }
    if (filterCat) list = list.filter(i => i.category === filterCat);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [ingredients, search, filterCat]);

  const categories = useMemo(() => {
    const cats = new Set(ingredients.map(i => i.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [ingredients]);

  // ── CRUD ──
  const handleSave = async (data: Partial<Ingredient> & { name: string }) => {
    setSaving(true);
    try {
      if (editing) {
        await apiFetch(`/api/menu/ingredients/${editing.id}`, {
          method: 'PUT', body: JSON.stringify(data),
        });
        toast.success('Updated', `${data.name} price updated.`);
      } else {
        await apiFetch('/api/menu/ingredients', {
          method: 'POST', body: JSON.stringify({ ...data, id: crypto.randomUUID() }),
        });
        toast.success('Added', `${data.name} added to pantry.`);
      }
      setEditing(null); setShowAdd(false);
      await load(); loadAlerts();
    } catch { toast.error('Error', 'Save failed.'); }
    setSaving(false);
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete "${name}" from pantry? This cannot be undone.`)) return;
    try {
      await apiFetch(`/api/menu/ingredients/${id}`, { method: 'DELETE' });
      toast.success('Deleted', `${name} removed.`);
      await load();
    } catch { toast.error('Error', 'Delete failed.'); }
  };

  // ── Bulk Import (Excel/CSV) ──
  const handleBulkImport = async (file: File) => {
    try {
      const XLSX = await import('xlsx-js-style');
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(ws);
      const items = rows.map(r => ({
        name: String(r['Name'] || r['name'] || ''),
        purchaseUnit: String(r['Unit'] || r['purchaseUnit'] || r['purchase_unit'] || 'kg') as PurchaseUnit,
        purchasePrice: Number(r['Price'] || r['purchasePrice'] || r['purchase_price'] || 0),
        category: String(r['Category'] || r['category'] || ''),
      })).filter(i => i.name);

      if (items.length === 0) { toast.error('Empty', 'No valid rows found in file.'); return; }

      const res = await apiFetch<{ data: { created: number; updated: number } }>('/api/menu/ingredients/bulk', {
        method: 'POST', body: JSON.stringify({ items }),
      });
      toast.success('Bulk Import Complete', `${res.data.created} created, ${res.data.updated} updated.`);
      await load(); loadAlerts();
    } catch { toast.error('Import Failed', 'Check file format: Name, Unit, Price, Category columns.'); }
  };

  // ── Export Template ──
  const handleExportTemplate = async () => {
    const XLSX = await import('xlsx-js-style');
    const wsData = [
      ['Name', 'Unit', 'Price', 'Category'],
      ...ingredients.map(i => [i.name, i.purchaseUnit, i.purchasePrice, i.category || '']),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ingredients');
    XLSX.writeFile(wb, 'Ingredient_Registry.xlsx');
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={24} className="animate-spin text-indigo-500" />
    </div>
  );

  return (
    <div className="space-y-5 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            🧂 Ingredient Registry
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Global Pantry — set current market prices for raw materials.
            <span className="ml-2 text-xs font-bold text-indigo-600">{ingredients.length} ingredient{ingredients.length !== 1 ? 's' : ''}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {priceChanges.length > 0 && (
            <button onClick={() => setShowAlerts(!showAlerts)}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl text-xs font-bold hover:bg-amber-100 transition-colors">
              <AlertTriangle size={13} /> {priceChanges.length} Price Change{priceChanges.length !== 1 ? 's' : ''}
            </button>
          )}
          <button onClick={handleExportTemplate}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50">
            <Download size={13} /> Export
          </button>
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50">
            <Upload size={13} /> Bulk Import
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={e => { if (e.target.files?.[0]) handleBulkImport(e.target.files[0]); e.target.value = ''; }} />
          <button onClick={() => { setEditing(null); setShowAdd(true); }}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 shadow-sm">
            <Plus size={14} /> Add Ingredient
          </button>
        </div>
      </div>

      {/* Price Change Alerts */}
      {showAlerts && priceChanges.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
          <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Recent Price Changes</p>
          <div className="space-y-1 max-h-40 overflow-auto">
            {priceChanges.slice(0, 20).map(pc => {
              const pct = pc.old_price > 0 ? ((pc.new_price - pc.old_price) / pc.old_price * 100).toFixed(1) : '—';
              const up = pc.new_price > pc.old_price;
              return (
                <div key={pc.id} className="flex items-center gap-3 text-xs py-1">
                  <span className="font-semibold text-slate-700 flex-1">{pc.ingredient_name}</span>
                  <span className="text-slate-400">{pc.old_price.toFixed(2)} →</span>
                  <span className={cn('font-bold', up ? 'text-red-600' : 'text-emerald-600')}>{pc.new_price.toFixed(2)}</span>
                  <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded-full', up ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')}>
                    {up ? '▲' : '▼'} {pct}%
                  </span>
                  <span className="text-[10px] text-slate-400">{new Date(pc.changed_at).toLocaleDateString()}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ingredients…"
            className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400" />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
          className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:border-indigo-400">
          <option value="">All Categories</option>
          {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Package size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm font-semibold text-slate-500">
            {ingredients.length === 0 ? 'No ingredients yet. Add your first raw material.' : 'No matching ingredients.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Ingredient</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Category</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Unit Price</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center">Unit</th>
                <th className="px-4 py-3 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(ing => (
                <tr key={ing.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-slate-800">{ing.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-500">{ing.category || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-slate-800">{ing.purchasePrice.toFixed(2)}</span>
                    <span className="text-[10px] text-slate-400 ml-1">QAR</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{ing.purchaseUnit}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditing(ing); setShowAdd(true); }}
                        className="p-1.5 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit">
                        <Edit2 size={13} className="text-indigo-500" />
                      </button>
                      <button onClick={() => handleDelete(ing.id, ing.name)}
                        className="p-1.5 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                        <Trash2 size={13} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAdd && (
        <IngredientFormModal
          ingredient={editing}
          saving={saving}
          onSave={handleSave}
          onClose={() => { setShowAdd(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

// ─── Ingredient Form Modal ──────────────────────────────────────────────────

function IngredientFormModal({ ingredient, saving, onSave, onClose }: {
  ingredient: Ingredient | null;
  saving: boolean;
  onSave: (data: Partial<Ingredient> & { name: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(ingredient?.name || '');
  const [unit, setUnit] = useState<PurchaseUnit>(ingredient?.purchaseUnit || 'kg');
  const [price, setPrice] = useState(String(ingredient?.purchasePrice ?? ''));
  const [category, setCategory] = useState(ingredient?.category || '');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{ingredient ? 'Edit Ingredient' : 'Add Ingredient'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><X size={18} className="text-slate-400" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Chicken Breast" autoFocus
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Purchase Unit</label>
              <select value={unit} onChange={e => setUnit(e.target.value as PurchaseUnit)}
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400">
                {PURCHASE_UNITS.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Price per Unit (QAR) *</label>
              <input type="number" step="0.01" min="0" value={price} onChange={e => setPrice(e.target.value)} placeholder="0.00"
                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Category</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400">
              <option value="">— Select Category —</option>
              {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-200">Cancel</button>
          <button onClick={() => { if (!name.trim()) return; onSave({ name: name.trim(), purchaseUnit: unit, purchasePrice: Number(price) || 0, category: category || undefined }); }}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            {ingredient ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}
